import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Handshake, ShieldCheck, TrendingUp, MapPinned, Award } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const TRUST_ICONS = [ShieldCheck, TrendingUp, MapPinned, Award];

const PartnerCTA = () => {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const p = t.homePartner;

  return (
    <section className="relative overflow-hidden bg-[#070D1B] py-20 md:py-28" data-testid="home-partner-cta">
      {/* Ambient gold glow */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#D4AF37]/10 blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[400px] bg-[#D4AF37]/[0.05] blur-[120px]" />
      </div>

      {/* Subtle GCC skyline silhouette */}
      <svg
        className="absolute bottom-0 inset-x-0 w-full h-28 md:h-36 text-[#D4AF37]/[0.07]"
        viewBox="0 0 1440 160"
        preserveAspectRatio="none"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M0 160 V120 H40 V90 H70 V120 H110 V70 H120 V40 L130 25 L140 40 V70 H150 V120 H210 V100 H250 V60 H262 V35 L272 22 L282 35 V60 H294 V100 H340 V130 H420 V95 H455 V125 H520 V80 H534 V50 L545 36 L556 50 V80 H570 V125 H640 V108 H690 V70 H702 V42 L713 28 L724 42 V70 H736 V108 H800 V130 H880 V96 H915 V126 H980 V82 H994 V52 L1005 38 L1016 52 V82 H1030 V126 H1100 V104 H1150 V66 H1162 V40 L1173 26 L1184 40 V66 H1196 V104 H1260 V128 H1340 V94 H1375 V124 H1440 V160 Z" />
      </svg>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Main banner */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="relative border border-[#D4AF37]/30 bg-gradient-to-br from-[#12213B] via-[#0B1426] to-[#0A1730] px-7 py-10 md:px-14 md:py-14 shadow-[0_30px_90px_-25px_rgba(0,0,0,0.85)]"
          data-testid="partner-cta-banner"
        >
          {/* Gold hairlines */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          <div className="absolute top-0 start-0 w-16 h-16 border-t-2 border-s-2 border-[#D4AF37]/50" />
          <div className="absolute bottom-0 end-0 w-16 h-16 border-b-2 border-e-2 border-[#D4AF37]/50" />

          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
            {/* Luxury gold badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="shrink-0"
            >
              <div className="relative w-24 h-24 md:w-32 md:h-32 grid place-items-center bg-gradient-to-br from-[#F0D27A] via-[#D4AF37] to-[#A8801F] shadow-[0_0_50px_rgba(212,175,55,0.45)]">
                <div className="absolute inset-1.5 border border-[#0A1223]/25" />
                <Handshake size={56} strokeWidth={1.7} className="text-[#0A1223]" />
              </div>
            </motion.div>

            {/* Text block */}
            <div className="flex-1 text-center lg:text-start">
              <p className="section-label text-[#D4AF37] mb-3">{p.eyebrow}</p>
              <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-white leading-[1.12]">
                {p.heading}
              </h2>
              <p className="mt-5 text-base md:text-lg text-slate-200 max-w-2xl mx-auto lg:mx-0">{p.text}</p>
              <p className="mt-2.5 text-sm md:text-base text-slate-400 max-w-2xl mx-auto lg:mx-0">{p.support}</p>
            </div>

            {/* CTA */}
            <div className="shrink-0">
              <button
                onClick={() => navigate("/partners")}
                data-testid="home-partner-cta-btn"
                className="group relative inline-flex items-center gap-3 px-9 py-4 md:px-10 md:py-5 bg-[#0A1223] border-2 border-[#D4AF37] text-[#D4AF37] font-bold text-base md:text-lg tracking-wide transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#0A1223] shadow-[0_0_28px_rgba(212,175,55,0.22)] hover:shadow-[0_0_48px_rgba(212,175,55,0.5)]"
              >
                {p.cta}
                <ArrowRight size={20} className={`transition-transform duration-300 group-hover:translate-x-1 ${isRTL ? "rotate-180 group-hover:-translate-x-1" : ""}`} />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Trust strip */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="partner-trust-strip">
          {(p.trust || []).map((item, i) => {
            const Icon = TRUST_ICONS[i] || ShieldCheck;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
                className="flex items-center gap-3.5 bg-[#0E1A30]/70 border border-white/10 px-5 py-4 hover:border-[#D4AF37]/40 transition-colors duration-300"
                data-testid={`partner-trust-${i}`}
              >
                <span className="w-11 h-11 grid place-items-center bg-[#D4AF37]/12 text-[#D4AF37] shrink-0">
                  <Icon size={22} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-white leading-tight">{item.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{item.sub}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PartnerCTA;
